import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Admin, Resource, Loading } from "react-admin";
import { FirebaseDataProvider, FirebaseAuthProvider } from "react-admin-firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "./i18n/en";
import { firebaseConfig } from "./firebaseConfig";
import LoginPage from "./LoginPage";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme, { crearTema } from './theme';
import { applyTheme, useDarkMode } from './shared/theme';
import './adminTheme.css';
import CustomAppBar from './components/CustomAppBar';
import { Layout } from 'react-admin';
import { auth, db as firestore } from "./shared/firebase";
import { conCasosDelEspejo } from "./providers/cofidisDesdeEspejo";

// Confidis Case components
import { CofidisCaseList } from "./cases/CofidisCaseList";
import { HipotecasCaseList } from "./cases/HipotecasCaseList";
import { CofidisCaseEdit } from "./cases/CofidisCaseEdit";
import { CofidisCaseEditGeneral } from "./cases/CofidisCaseEditGeneral";
import { CofidisCaseEditCredits } from "./cases/CofidisCaseEditCredits";
import { CofidisCaseEditWrapper } from "./cases/CofidisCaseEditWrapper";
import { CofidisCaseCreate } from "./cases/CofidisCaseCreate";
import { CofidisCaseShow } from "./cases/CofidisCaseShow";

// Form Simple (simplified calculator form leads)
import { FormSimpleList } from "./cases/FormSimpleList";
import { FormSimpleShow } from "./cases/FormSimpleShow";

// Manual affiliate API sends (single contact or Excel/CSV upload)
import ApiManualPage from "./components/ApiManualPage";



// NOTE: ClientPortal and Calculator have their own independent entry points
// (index.client.js and index.calculator.js) and are NOT imported here.
// This App component only serves Admin and Creditor views.

// Custom Loading component with personalized messages
const CustomLoading = () => (
  <Loading
    loadingPrimary="Loading your application..."
    loadingSecondary="Please wait while we prepare everything for you."
  />
);

// i18n provider
const i18nProvider = polyglotI18nProvider(() => englishMessages, "en");

// Firebase options
const firebaseOptions = {
  logging: true, // Enable logging
  persistence: 'local',
  softDelete: false,
  metaFieldCasing: 'camel',
  associateUsersById: true,
};


// Initialize Firebase app

/* Providers are created inside App after Firebase bootstraps.
const dataProvider = FirebaseDataProvider(firebaseConfig, {
  ...firebaseOptions,
  logging: (message, ...args) => {
    console.log(`🔥raf: ${message}`, ...args);
  }
}); */

// Layout configuration
const CustomLayout = {
  appBar: CustomAppBar,
  sx: {
    '& .RaLayout-content': {
      backgroundColor: theme.palette.background.default,
      padding: 3,
    },
  },
};

function App({ view = 'admin' }) {
  const [dataProvider, setDataProvider] = useState(null);
  const [authProvider, setAuthProvider] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCreditorView = view === 'creditor';
  const [authReady, setAuthReady] = useState(false);
  // Modo oscuro (André, 28/08). Solo para el panel interno: el portal de
  // acreedores es de cara al cliente y se queda como está.
  const [prefiereOscuro, alternarOscuro] = useDarkMode();
  const oscuro = prefiereOscuro && !isCreditorView;
  const tema = useMemo(
    () => crearTema(oscuro ? 'dark' : 'light', isCreditorView ? 'creditor' : 'admin'),
    [oscuro, isCreditorView]
  );

  console.log("Current view:", view, "isCreditorView:", isCreditorView);

  useEffect(() => {
    if (isCreditorView) applyTheme(false);
  }, [isCreditorView]);

  // Set document title based on view
  useEffect(() => {
    const title = isCreditorView
      ? "Creditor Portal | EasyDebt"
      : "EasyDebt | Admin Portal";
    document.title = title;
  }, [isCreditorView]);

  useEffect(() => {
    const initializeProviders = async () => {
      try {
        // Import the readOnly wrapper only when needed
        // Import the readOnly wrapper only when needed
        let dp = FirebaseDataProvider(firebaseConfig, firebaseOptions);
        // El CSV de casos Cofidis sale del VPS (sin build con la URL, nada cambia).
        dp = conCasosDelEspejo(dp);

        // Apply read-only wrapper for creditor view
        if (isCreditorView) {
          const { readOnly } = await import('./providers/readOnlyWrapper');
          dp = readOnly(dp);
        }


        // Apply read-only wrapper for creditor view
        if (isCreditorView) {
          const { readOnly } = await import('./providers/readOnlyWrapper');
          dp = readOnly(dp);
        }

        const ap = FirebaseAuthProvider(firebaseConfig, firebaseOptions);

        const myAuthProvider = {
          ...ap,
          // Login: ensure Firebase Auth state is set
          login: async (params) => {
            try {
              const result = await ap.login(params);

              // Wait for Firebase Auth state to be set
              await new Promise((resolve) => {
                const unsubscribe = onAuthStateChanged(auth, (user) => {
                  if (user) {
                    unsubscribe();
                    resolve();
                  }
                });
              });

              // Get user document from Firestore
              const userDoc = await getDoc(doc(firestore, "users", auth.currentUser.uid));
              if (!userDoc.exists()) {
                throw new Error("User document not found in Firestore");
              }

              // Update the result with user data
              return {
                ...result,
                id: auth.currentUser.uid,
                fullName: userDoc.data().displayName || '',
                role: userDoc.data().role,
                avatar: '',
              };
            } catch (error) {
              console.error("Login error:", error);
              throw error;
            }
          },

          // Get identity with role
          getIdentity: async () => {
            try {
              if (!auth.currentUser) {
                throw new Error("No authenticated user");
              }

              const userDoc = await getDoc(doc(firestore, "users", auth.currentUser.uid));
              if (!userDoc.exists()) {
                throw new Error("User document not found");
              }

              return {
                id: auth.currentUser.uid,
                fullName: userDoc.data().displayName || '',
                role: userDoc.data().role,
                avatar: '',
              };
            } catch (error) {
              console.error("Get identity error:", error);
              throw error;
            }
          },

          // Logout: ensure Firebase Auth state is cleared
          logout: async () => {
            const result = await ap.logout();
            await auth.signOut();
            return result;
          },

          // Check auth: verify both React-Admin and Firebase Auth states
          checkAuth: async () => {
            // If Firebase already has a user, we're good
            if (auth.currentUser) return Promise.resolve();

            // Otherwise wait (max 3 s) for Firebase to report
            return new Promise((resolve, reject) => {
              const timer = setTimeout(() => reject(new Error('timeout')), 3000);
              const unsub = onAuthStateChanged(auth, (user) => {
                clearTimeout(timer);
                unsub();
                user ? resolve() : reject(new Error('No user'));
              });
            });
          },

          // Get permissions (user role)
          getPermissions: async () => {
            try {
              // Ensure Firebase user is authenticated
              if (!auth.currentUser) {
                console.log("No Firebase user found");
                return "";
              }

              // Get user document from Firestore
              const userDoc = await getDoc(doc(firestore, "users", auth.currentUser.uid));
              console.log("User document:", userDoc.exists() ? userDoc.data() : "not found");

              if (!userDoc.exists()) {
                console.log("User document not found in Firestore");
                return "";
              }

              const role = userDoc.data().role;
              console.log("User role from Firestore:", role);

              if (!role) {
                console.log("No role found in user document");
                return "";
              }

              return role;
            } catch (error) {
              console.error("Failed to get user permissions:", error);
              console.error("Error details:", {
                code: error.code,
                message: error.message,
                stack: error.stack
              });
              return "";
            }
          },

          // Handle permission-denied errors
          checkError: (error) => {
            if (error?.code === "permission-denied") {
              return Promise.resolve();
            }
            return ap.checkError(error);
          },
        };

        setDataProvider(dp);
        setAuthProvider(myAuthProvider);
        setLoading(false);
      } catch (error) {
        console.error("Error initializing providers:", error);
        setLoading(false);
      }
    };

    initializeProviders();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      setAuthReady(true);       // Firebase has hydrated
    });
    return unsubscribe;
  }, []);

  // Add Firebase Auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Firebase Auth state changed:", user ? "signed in" : "signed out");
      if (!user && authProvider) {
        // If Firebase Auth state is signed out but React-Admin thinks we're signed in,
        // trigger a React-Admin logout
        try {
          await authProvider.logout();
        } catch (error) {
          console.error("Error during auto-logout:", error);
        }
      }
    });

    return () => unsubscribe();
  }, [authProvider]);

  // Wait for both Firebase *and* the providers
  // La barra lleva el interruptor de claro/oscuro; se memoriza para que el
  // layout no la vuelva a montar en cada repintado.
  const BarraSuperior = useCallback(
    (props) => <CustomAppBar {...props} oscuro={oscuro} alternarOscuro={isCreditorView ? null : alternarOscuro} />,
    [oscuro, isCreditorView, alternarOscuro]
  );

  if (loading || !authReady) {
    return <CustomLoading />;
  }

  if (!dataProvider || !authProvider) {
    return <div>Failed to initialize application. Please refresh the page.</div>;
  }

  return (
    <ThemeProvider theme={tema}>
      <CssBaseline />
      <Admin
        theme={tema}
        title={isCreditorView ? "Creditor Portal | EasyDebt" : "EasyDebt | Admin Portal"}
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        loginPage={() => <LoginPage view={view} />}
        loadingComponent={CustomLoading}
        requireAuth
        layout={props => (
          <Layout
            {...props}
            appBar={BarraSuperior}
            sx={{
              '& .RaLayout-content': {
                backgroundColor: tema.palette.background.default,
                padding: 3,
              },
            }}
          />
        )}
      >
        {/* Resources based on permissions and view type */}
        <Resource
          name="cofidisCases"
          list={CofidisCaseList}
          show={CofidisCaseShow}
          edit={!isCreditorView ? CofidisCaseEditWrapper : undefined}
          create={!isCreditorView ? CofidisCaseCreate : undefined}
        />
        {!isCreditorView && (
          <Resource
            name="hipotecas"
            options={{ label: "Hipotecas" }}
            list={HipotecasCaseList}
          />
        )}
        {!isCreditorView && (
          <Resource
            name="formSimpleLeads"
            options={{ label: "Form Simple" }}
            list={FormSimpleList}
            show={FormSimpleShow}
          />
        )}
        {!isCreditorView && (
          <Resource
            name="apiManualSends"
            options={{ label: "API Manual" }}
            list={ApiManualPage}
          />
        )}
      </Admin>
    </ThemeProvider>
  );
}

export default App;
